import logger from '@eventbrite/client-logger';
import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import React, { useEffect, useState } from 'react';

// InPlayer requires window so we only load this in client-side
const InPlayer = HAS_WINDOW ? require('@inplayer-org/inplayer.js') : null;

export interface Props {
    data?: {
        assetId: { text: string };
        title: { text: string };
        accessToken: { text: string };
        accessTokenExpiration: { text: string };
        clientId: { text: string };
        hasClientSecret: { value: boolean };
    };
    loadInPlayer?: Function;
    loadAssetPaywallScript?: Function;
}

export const InplayerModule: React.FC<Props> = (props) => {
    const [state, setState] = useState({
        isLoading: true,
        isError: false,
    });

    const {
        data: {
            assetId = { text: '' },
            title = { text: '' },
            hasClientSecret = { value: false },
        } = {},
    } = props;

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (!hasClientSecret.value) {
            return;
        }

        const loadInPlayerScript =
            props.loadInPlayer ||
            (() =>
                loadInPlayer({
                    accessToken: props?.data?.accessToken?.text || '',
                    accessTokenExpiration:
                        props.data?.accessTokenExpiration?.text || '',
                    clientId: props.data?.clientId?.text || '',
                    assetId: props.data?.assetId?.text || '',
                    loadAssetPaywallScript:
                        props.loadAssetPaywallScript || loadAssetPaywallScript,
                }));

        let isRendered = true;

        loadInPlayerScript()
            .then(() => {
                if (!isRendered) {
                    return;
                }

                setState({ isLoading: false, isError: state.isError });
            })
            .catch((err: any) => {
                logger.error(err);

                if (!isRendered) {
                    return;
                }

                setState({ isLoading: false, isError: true });
            });

        return () => {
            isRendered = false;
        };
    }, []);
    /* eslint-enable */

    const { isLoading, isError } = state;

    if (!hasClientSecret.value) {
        return null;
    }

    return (
        <div className="dc-modules__item--inplayer">
            <h2
                className="eds-l-mar-bot-4 eds-text-bl"
                data-spec="inplayer-title"
            >
                {title.text}
            </h2>
            <div className="eds-l-mar-top-2 dc-modules__item--inplayer__content">
                {isLoading && (
                    <div
                        className="dc-modules__item--inplayer__loader"
                        data-testid="progress"
                    >
                        <ProgressIndicator style="gradient" />
                    </div>
                )}
                {isError && gettext('There is an error loading this module')}
                <div
                    id={`inplayer-${assetId.text}`}
                    className="inplayer-paywall"
                    data-spec="inplayer-paywall"
                ></div>
            </div>
        </div>
    );
};

interface AssetConnection {
    accessToken: string;
    accessTokenExpiration: string;
    clientId: string;
    assetId: string;
    loadAssetPaywallScript: Function;
}

function loadInPlayer({
    accessToken,
    accessTokenExpiration,
    clientId,
    assetId,
    loadAssetPaywallScript,
}: AssetConnection) {
    return loadAssetPaywallScript()
        .then(() =>
            InPlayer.Account.setToken(accessToken, '', accessTokenExpiration),
        )
        .then(() =>
            InPlayer.Asset.checkAccessForAsset(
                assetId,
                InPlayer.Account.getToken(),
            ).catch((error: any) =>
                error.response.json().then((data: any) => {
                    throw data;
                }),
            ),
        )
        .then(
            () =>
                new Promise((resolve, reject) => {
                    const { InplayerPaywall } = window as any;
                    const paywall = new InplayerPaywall(clientId, [
                        { id: assetId },
                    ]);
                    paywall.on('error', (err: any) => reject(err));
                    paywall.on('init', () => resolve(paywall));
                }),
        );
}

function loadAssetPaywallScript() {
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.dataset.spec = 'inplayer-paywall-script';
        script.type = 'text/javascript';
        script.src =
            'https://assets.inplayer.com/paywall/latest/paywall.min.js';
        document.body.appendChild(script);
        script.onload = resolve;
    });
}
